/**
 * PM - Conteos del bloque Disponibilidad (colecciones y búsqueda).
 * Rediseño 2026-10-01, versión de RENDIMIENTO.
 *
 * snippets/pm-collection-filter-availability.liquid ya trae los counts
 * nativos de Shopify en el HTML (cero requests). Los que no son confiables
 * en el estado actual nacen con data-pm-pending (ver ese snippet). Este
 * archivo:
 *  1. guarda en una cache en memoria los counts conocidos, por combinación
 *     de "otros filtros" (sin disponibilidad, estado comercial, página ni
 *     orden): window.__pmAvailCountsCache;
 *  2. completa los pendientes desde esa cache -- instantáneo en cada clic,
 *     porque la vista anterior ya los conocía;
 *  3. SOLO si sigue faltando alguno (enlace directo a un estado filtrado, sin
 *     vista previa), hace UNA consulta a sections/pm-collection-
 *     availability-index.liquid (?view=pm-availability-index, todas las
 *     páginas) y la guarda en la cache.
 * Filas Preventa / Compra internacional: visibles si su count > 0 o si su
 * casilla está activa como filtro.
 *
 * Custom element: assets/facets.js reemplaza el sidebar por innerHTML en cada
 * respuesta AJAX; connectedCallback() se re-dispara en cada reinserción.
 */
(function () {
  const INDEX_VIEW = 'pm-availability-index';
  const INDEX_SELECTOR = '#PMCollectionAvailabilityIndex';
  const KEYS = ['disponible', 'preventa', 'intl', 'agotado'];

  if (!window.__pmAvailCountsCache) {
    window.__pmAvailCountsCache = new Map();
  }
  const cache = window.__pmAvailCountsCache;
  let activeController = null;

  function getBaseParams() {
    const params = new URLSearchParams(window.location.search);
    ['filter.v.availability', 'filter.p.m.promusic.commercial_status', 'page', 'view', 'section_id', 'sections', 'sort_by', 'preview_theme_id'].forEach((key) => params.delete(key));
    params.sort();
    return params;
  }

  function getQueryKey() {
    return window.location.pathname + '?' + getBaseParams().toString();
  }

  function countNodes() {
    const nodes = {};
    KEYS.forEach((key) => {
      nodes[key] = document.querySelector('[data-pm-count="' + key + '"]');
    });
    return nodes;
  }

  function readKnown(nodes) {
    const known = {};
    KEYS.forEach((key) => {
      const node = nodes[key];
      if (node && !node.hasAttribute('data-pm-pending')) {
        const value = Number.parseInt(node.textContent.trim(), 10);
        if (!Number.isNaN(value)) known[key] = value;
      }
    });
    return known;
  }

  function updateRow(node, value) {
    const row = node.closest('li');
    if (!row || !row.classList.contains('pm-availability-option--preventa') && !row.classList.contains('pm-availability-option--intl')) {
      return;
    }
    const input = row.querySelector('input[type="checkbox"]');
    const activeAsFilter = input && input.checked && row.hidden === false;
    row.hidden = value === 0 && !activeAsFilter;
  }

  function fill(nodes, values) {
    let missing = false;
    KEYS.forEach((key) => {
      const node = nodes[key];
      if (!node || !node.hasAttribute('data-pm-pending')) return;
      if (values[key] === undefined) {
        missing = true;
        return;
      }
      node.textContent = values[key];
      node.removeAttribute('data-pm-pending');
      if (key === 'agotado') {
        node.setAttribute('data-pm-agotado-count', String(values[key]));
        node.removeAttribute('data-pm-agotado-pending');
      }
      updateRow(node, values[key]);
    });
    return missing;
  }

  function buildIndexUrl(page) {
    const url = new URL(window.location.pathname, window.location.origin);
    const params = getBaseParams();
    params.set('view', INDEX_VIEW);
    params.set('page', String(page));
    url.search = params.toString();
    return url.toString();
  }

  function readInt(index, key) {
    return Number.parseInt(index.dataset[key] || '0', 10) || 0;
  }

  async function fetchPage(page, signal) {
    const response = await fetch(buildIndexUrl(page), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal,
    });
    if (!response.ok) {
      throw new Error(`Availability index request failed (${response.status})`);
    }
    const html = await response.text();
    const index = new DOMParser().parseFromString(html, 'text/html').querySelector(INDEX_SELECTOR);
    if (!index) {
      throw new Error('Promusic availability index was not found');
    }
    return {
      pages: Number.parseInt(index.dataset.pages || '1', 10) || 1,
      disponible: readInt(index, 'disponibleCount'),
      preventa: readInt(index, 'preventaCount'),
      intl: readInt(index, 'intlCount'),
      agotado: readInt(index, 'agotadoCount'),
    };
  }

  async function fetchTotals(signal) {
    const first = await fetchPage(1, signal);
    const totals = { disponible: first.disponible, preventa: first.preventa, intl: first.intl, agotado: first.agotado };
    if (first.pages > 1) {
      const extra = Array.from({ length: first.pages - 1 }, (_, i) => i + 2);
      const rest = await Promise.all(extra.map((page) => fetchPage(page, signal)));
      rest.forEach((chunk) => KEYS.forEach((key) => { totals[key] += chunk[key]; }));
    }
    return totals;
  }

  async function refresh() {
    const key = getQueryKey();
    const nodes = countNodes();
    const known = readKnown(nodes);
    const cached = Object.assign({}, cache.get(key) || {}, known);
    cache.set(key, cached);

    const missing = fill(nodes, cached);
    if (!missing) {
      document.dispatchEvent(new CustomEvent('pm:availability-counts-updated'));
      return;
    }

    if (activeController) activeController.abort();
    const controller = new AbortController();
    activeController = controller;
    try {
      const totals = await fetchTotals(controller.signal);
      if (controller.signal.aborted) return;
      // Los counts nativos conocidos tienen prioridad sobre el índice.
      const merged = Object.assign({}, totals, cache.get(key) || {});
      cache.set(key, merged);
      fill(countNodes(), merged);
      document.dispatchEvent(new CustomEvent('pm:availability-counts-updated'));
    } catch (error) {
      if (error && error.name === 'AbortError') return;
      console.warn('[Promusic] Availability counts skipped:', error);
    }
  }

  class PmCollectionCountsScope extends HTMLElement {
    connectedCallback() {
      refresh();
    }
  }

  if (!customElements.get('pm-collection-counts-scope')) {
    customElements.define('pm-collection-counts-scope', PmCollectionCountsScope);
  }
})();
